<template>
  <div class="flex justify-between">
    <va-page-title>{{ t("sellIn") }}</va-page-title>

    <div class="text-right">
      <div>
        <va-button
          :disabled="!ordersStore.canCreate"
          @click="router.push('/sell-in/create')"
        >
          <i class="rm rm-ico-e-add" />
        </va-button>
      </div>
      <div class="mt-4 flex items-center">
        <span class="text-gray-500">{{ t("filterCreationDate") }}</span>
        <div class="ml-4">
          <va-date-picker
            v-model="dateRanges[0].fromDate"
            :placeholder="t('from')"
            day-target="start"
            @change="onFilterDateChange"
          />
        </div>
        <div class="ml-4">
          <va-date-picker
            v-model="dateRanges[0].toDate"
            :placeholder="t('to')"
            day-target="end"
            @change="onFilterDateChange"
          />
        </div>
      </div>
    </div>
  </div>
  <va-table
    :data-source="ordersStore.items"
    :table-config="sellInTableConfig"
    :search-options="ordersStore.searchOptions"
    :loading="ordersStore.isLoading"
    class="w-100"
    @update="ordersStore.updateSearchOptions"
  >
    <template #bodyCell="{ column, text, record }">
      <template v-if="column.dataIndex === 'code'">
        <router-link :to="`/sell-in/${record.id}`" class="text-indigo-600">
          {{ text }}
        </router-link>
      </template>
      <template v-if="column.dataIndex === 'buyer'">
        {{ text.name }}
      </template>
      <template v-if="column.dataIndex === 'vendor'">
        {{ text.name }}
      </template>
      <template v-if="column.dataIndex === 'refCode'">
        {{ record.refCode }}
      </template>
      <template v-if="column.dataIndex === 'creationDate'">
        {{ formatDate(record.creationDate) }}
      </template>
      <template v-if="column.dataIndex === 'status'">
        <a-tag :color="statusColors(record.status)">
          {{ t(record.status) }}
        </a-tag>
      </template>
      <template v-if="column.dataIndex === 'isInvoiced'">
        <div class="text-center">
          <va-check-icon :check="text" />
        </div>
      </template>
      <template v-if="column.dataIndex === 'isPaid'">
        <div class="text-center">
          <va-check-icon :check="text" />
        </div>
      </template>
      <template v-if="column.dataIndex === 'total'">
        <div class="text-right">
          {{ useFormatCurrency(text, record.currency) }}
        </div>
      </template>
    </template>
    <template #footer>
      <div class="text-right font-bold">
        {{ t("total") }}:
        {{ useFormatCurrency(total, ordersStore.items[0]?.currency) }}
      </div>
    </template>
  </va-table>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import VaTable from "@/components/organisms/VaTable.vue";
import { Order, tableConfig } from "@/models/order.interface";
import VaPageTitle from "@/components/molecules/VaPageTitle.vue";
import router from "@/router";
import { useI18n } from "vue-i18n";
import VaButton from "@/components/molecules/VaButton.vue";
import { useFormatDate, useFormatCurrency } from "@/composables/formatters";
import { cloneDeep } from "lodash";
import VaCheckIcon from "@/components/atoms/VaCheckIcon.vue";
import { useOrdersStore } from "@/stores/orders";
import { FilterDateRange } from "@/models/listable.interface";
import VaDatePicker from "@/components/molecules/VaDatePicker.vue";
import { statusColors } from "@/models/orderDetails.interface";
import { useMeta } from "vue-meta";

useMeta({
  title: "Orders Sell In",
});
const ordersStore = useOrdersStore();
const { t } = useI18n();
const formatDate = useFormatDate;

const searchOptions = computed(() => ordersStore.searchOptions);

const sellInTableConfig = cloneDeep(tableConfig);
delete sellInTableConfig.buyer;

const dateRanges = ref([
  {
    field: "creationDate",
    fromDate: undefined,
    toDate: undefined,
  },
] as FilterDateRange[]);

const total = computed(() =>
  ordersStore.items.reduce(function (a: number, b: Order) {
    return a + (b.total || 0);
  }, 0)
);

// const statuscolor = ()

onBeforeMount(() => {
  ordersStore.setPreSort(
    {
      columnKey: "creationDate",
      order: "descend",
    },
    true
  );
});

function onFilterDateChange() {
  searchOptions.value.filterBetweenDate = dateRanges.value;
  ordersStore.updateSearchOptions(searchOptions.value);
}
</script>
